import { ApplicationConfig, provideBrowserGlobalErrorListeners, LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localePtBr from '@angular/common/locales/pt';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { FilterMatchMode, MessageService } from 'primeng/api';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { productInterceptor } from './core/interceptors/product.interceptor';

registerLocaleData(localePtBr, 'pt-BR');

const SmartVisionPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#E8EDFF',
      100: '#C5D1FE',
      200: '#9BB0FE',
      300: '#6B8CFE',
      400: '#3D6AFE',
      500: '#063DFE',
      600: '#0534D9',
      700: '#042BB3',
      800: '#03228D',
      900: '#021968',
      950: '#010F42'
    }
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([authInterceptor, productInterceptor])),
    MessageService,
    providePrimeNG({
      theme: {
        preset: SmartVisionPreset,
        options: {
          darkModeSelector: '.app-dark',
        },
      },
      translation: {
        startsWith: 'Começa com',
        contains: 'Contém',
        notContains: 'Não contém',
        endsWith: 'Termina com',
        equals: 'Igual a',
        notEquals: 'Diferente de',
        noFilter: 'Sem filtro',
        lt: 'Menor que',
        lte: 'Menor ou igual a',
        gt: 'Maior que',
        gte: 'Maior ou igual a',
        is: 'É',
        isNot: 'Não é',
        before: 'Antes de',
        after: 'Depois de',
        dateIs: 'Data é',
        dateIsNot: 'Data não é',
        dateBefore: 'Data anterior a',
        dateAfter: 'Data posterior a',
        clear: 'Limpar',
        apply: 'Aplicar',
        matchAll: 'Corresponder todos',
        matchAny: 'Corresponder qualquer',
        addRule: 'Adicionar regra',
        removeRule: 'Remover regra',
        accept: 'Sim',
        reject: 'Não',
        choose: 'Escolher',
        cancel: 'Cancelar',
        dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
        dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
        dayNamesMin: ['Do', 'Se', 'Te', 'Qa', 'Qi', 'Sx', 'Sa'],
        monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
        monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
        today: 'Hoje',
        weekHeader: 'Sm',
        firstDayOfWeek: 0,
        dateFormat: 'dd/mm/yy',
        emptyFilterMessage: 'Nenhum resultado encontrado',
        emptyMessage: 'Nenhum resultado encontrado',
        aria: {
          trueLabel: 'Verdadeiro',
          falseLabel: 'Falso',
          nullLabel: 'Não selecionado',
          star: '1 estrela',
          stars: '{star} estrelas',
          selectAll: 'Todos selecionados',
          unselectAll: 'Todos desmarcados',
          close: 'Fechar',
          previous: 'Anterior',
          next: 'Próximo',
          navigation: 'Navegação',
          scrollTop: 'Ir ao topo',
          moveTop: 'Mover para o topo',
          moveUp: 'Mover para cima',
          moveDown: 'Mover para baixo',
          moveBottom: 'Mover para o final',
          moveToTarget: 'Mover para destino',
          moveToSource: 'Mover para origem',
          moveAllToTarget: 'Mover todos para destino',
          moveAllToSource: 'Mover todos para origem',
          pageLabel: '{page}',
          firstPageLabel: 'Primeira página',
          lastPageLabel: 'Última página',
          nextPageLabel: 'Próxima página',
          prevPageLabel: 'Página anterior',
          previousPageLabel: 'Página anterior',
          rowsPerPageLabel: 'Linhas por página',
          jumpToPageDropdownLabel: 'Ir para página',
          jumpToPageInputLabel: 'Ir para página',
          selectRow: 'Linha selecionada',
          unselectRow: 'Linha desmarcada',
          expandRow: 'Linha expandida',
          collapseRow: 'Linha recolhida',
          showFilterMenu: 'Mostrar filtro',
          hideFilterMenu: 'Ocultar filtro',
          filterOperator: 'Operador do filtro',
          filterConstraint: 'Restrição do filtro',
          editRow: 'Editar linha',
          saveEdit: 'Salvar edição',
          cancelEdit: 'Cancelar edição',
          listView: 'Visualização em lista',
          gridView: 'Visualização em grade',
        },
      },
      filterMatchModeOptions: {
        text: [FilterMatchMode.STARTS_WITH, FilterMatchMode.CONTAINS, FilterMatchMode.NOT_CONTAINS, FilterMatchMode.ENDS_WITH, FilterMatchMode.EQUALS, FilterMatchMode.NOT_EQUALS],
        numeric: [FilterMatchMode.EQUALS, FilterMatchMode.NOT_EQUALS, FilterMatchMode.LESS_THAN, FilterMatchMode.LESS_THAN_OR_EQUAL_TO, FilterMatchMode.GREATER_THAN, FilterMatchMode.GREATER_THAN_OR_EQUAL_TO],
        date: [FilterMatchMode.DATE_IS, FilterMatchMode.DATE_IS_NOT, FilterMatchMode.DATE_BEFORE, FilterMatchMode.DATE_AFTER],
      },
    }),
  ],
};
